"use client";

import {
  ConverterPanel,
  type ConverterStrings,
} from "@/components/tools/research-discovery/converter-panel";
import { useToolUi } from "@/components/tools/tool-ui";
import { convertNbib } from "@/lib/tool-kit/research-discovery/nbib";

/** English strings. A translated content file overrides these through `ui`. */
export const NBIB_CONVERTER_STRINGS = {
  inputLabel: "Your .nbib or MEDLINE file",
  inputPlaceholder:
    "PMID- 12345678\nTI  - Title of the article.\nFAU - Surname, Given\nAU  - Surname G",
  inputHint:
    "Paste the text or upload the file from PubMed's Save → Citation manager (.nbib) or its MEDLINE format. It is converted in your browser — nothing is uploaded.",
  upload: "Upload an .nbib file",
  uploadLabel: "Choose an .nbib or MEDLINE file to convert",
  trySample: "Try a sample",
  clear: "Clear",
  sample:
    "PMID- 25541969\nOWN - NLM\nSTAT- MEDLINE\nIS  - 1932-6203 (Electronic)\nVI  - 9\nIP  - 12\nDP  - 2014\nTI  - Scholarly context not found: one in five articles suffers from reference\n      rot.\nPG  - e115253\nLID - 10.1371/journal.pone.0115253 [doi]\nFAU - Klein, Martin\nAU  - Klein M\nFAU - Van de Sompel, Herbert\nAU  - Van de Sompel H\nLA  - eng\nPT  - Journal Article\nDEP - 20141226\nTA  - PLoS One\nJT  - PloS one\nPMC - PMC4281244\nMH  - *Access to Information\nMH  - Periodicals as Topic/standards/*statistics & numerical data\n\nPMID- 16275915\nOWN - NLM\nIS  - 0027-8424 (Print)\nVI  - 102\nIP  - 46\nDP  - 2005 Nov 15\nTI  - An index to quantify an individual's scientific research output.\nPG  - 16569-72\nAB  - I propose the index h, defined as the number of papers with citation number\n      > or =h, as a useful index to characterize the scientific output of a\n      researcher.\nFAU - Hirsch, J E\nAU  - Hirsch JE\nLA  - eng\nPT  - Journal Article\nTA  - Proc Natl Acad Sci U S A\nJT  - Proceedings of the National Academy of Sciences of the United States of America\nAID - 10.1073/pnas.0507655102 [doi]\n",
  fileRead: "Read {name} as {encoding}.",
  encodings: {
    utf8: "UTF-8",
    utf16: "UTF-16 — the converted file is saved as UTF-8",
    ansi: "Windows-1252, because it is not valid UTF-8 — check the accented letters",
  },
  emptyPrompt: "Paste or upload a PubMed .nbib file to convert it.",
  results: "Conversion",
  statFound: "Records found",
  statConverted: "Records converted",
  statDoi: "With a DOI",
  statWarnings: "Warnings",
  formatLabel: "Convert to",
  formats: { ris: "RIS", bibtex: "BibTeX", csl: "CSL JSON" },
  outputHeading: "Converted file",
  previewCut:
    "Showing the first {shown} of {total} lines. The download has the whole file.",
  copyOutput: "Copy",
  copied: "Copied!",
  download: "Download {file}",
  warningsHeading: "Things to check",
  warningsCut: "Showing the first {shown} of {total}.",
  warnings: {
    retracted:
      "PubMed marks this record ({detail}) as a retracted publication. Read the retraction notice before citing it.",
    retractionNotice:
      "This record ({detail}) is a retraction notice, not the retracted article itself.",
    noTitle: "This record has no title.",
    noAuthors:
      "This record has no authors, which is normal for some editorials and news items.",
    dateUnreadable:
      "The publication date “{detail}” has no year, so the date was left out.",
  },
  whereRecord: "Record {record}",
  whereFile: "Whole file",
  problems: {
    empty: "Paste or upload a PubMed .nbib file to convert it.",
    noRecords:
      "No PubMed records were found. Each record should start with a PMID line such as “PMID- 25541969”. In PubMed, use Save, choose Citation manager and download the .nbib file.",
    looksLikeRis:
      "This looks like a RIS file, not a PubMed .nbib file. RIS needs no conversion — try the RIS File Validator instead.",
  },
  tableHeading: "Records",
  tableCut: "Showing the first {shown} of {total} records.",
  columns: { type: "Type", authors: "Authors", year: "Year", title: "Title" },
  etAl: "et al.",
  untitled: "Untitled",
} as const satisfies ConverterStrings;

export default function NbibConverterWidget() {
  const strings = useToolUi(NBIB_CONVERTER_STRINGS);
  return (
    <ConverterPanel
      accept=".nbib,.txt,.med,text/plain"
      baseName="pubmed-references"
      convert={convertNbib}
      idPrefix="nbc"
      strings={strings}
    />
  );
}
